import React, { useState } from 'react';

interface QuestionnaireProps {
  open: boolean;
  onClose: () => void;
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [childName, setChildName] = useState('');
  const [goals, setGoals] = useState<string[]>([]);
  const [timeline, setTimeline] = useState('');
  const [frequency, setFrequency] = useState('');
  const [support, setSupport] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [website, setWebsite] = useState('');

  const toggleGoal = (g: string) => {
    setGoals(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  const next = () => {
    const requiredAnswerMissing = (
      (step === 0 && !childName.trim()) ||
      (step === 2 && !timeline) ||
      (step === 3 && !frequency) ||
      (step === 4 && !support)
    );
    if (requiredAnswerMissing) {
      setStatus('error');
      setErrorMessage('Please answer this question before continuing.');
      return;
    }
    setStatus('idle');
    setErrorMessage('');
    setStep(current => Math.min(current + 1, 5));
  };
  const prev = () => {
    setStatus('idle');
    setErrorMessage('');
    setStep(current => Math.max(current - 1, 0));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending' || status === 'sent') return;
    setStatus('sending');
    setErrorMessage('');
    try {
      const response = await fetch('/api/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'questionnaire',
          name: childName,
          goals: goals.join(', ') || 'Not selected',
          timeline,
          frequency,
          support,
          website,
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(typeof result?.error === 'string' ? result.error : 'Unable to submit your questionnaire. Please try again.');
      }
      setStatus('sent');
      window.setTimeout(onClose, 1200);
    } catch (submitError) {
      setStatus('error');
      setErrorMessage(submitError instanceof Error ? submitError.message : 'Unable to submit your questionnaire. Please try again.');
    }
  };

  if (!open) return null;

  const goalOptions = [
    'Academic skills',
    'Confidence & motivation',
    'Focus & learning habits',
    'Independence & self-regulation',
    'Behaviour & social skills',
    'Overall development',
  ];

  const timelineOptions = ['Within 3 months', 'Within 6 months', 'Within 12 months', 'Long-term / ongoing'];
  const frequencyOptions = ['Once a week', 'Twice a week', '3+ times a week'];
  const supportOptions = [
    'Little to none — I cannot reliably support homework',
    'Minimal — I can ensure homework is completed',
    'High — I can provide assistance and monitor homework',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="questionnaire-title"
        className="relative max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto overscroll-contain rounded-2xl bg-white shadow-xl"
      >
        <div className="p-4 sm:p-6 md:p-8">
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="questionnaire-website">Leave this field empty</label>
            <input
              id="questionnaire-website"
              name="website"
              type="text"
              autoComplete="off"
              tabIndex={-1}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between mb-2">
            <h2 id="questionnaire-title" className="min-w-0 pr-2 text-xl font-bold text-black">
              What&apos;s Your Child&apos;s ETA to Their Learning Goals?
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close questionnaire"
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue"
            >
              &times;
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Answer 5 quick questions so we can recommend their ETA and their ideal package.
          </p>

          <div className="mb-4">
            <div className="flex gap-1" role="progressbar" aria-label="Questionnaire progress" aria-valuemin={0} aria-valuemax={5} aria-valuenow={Math.min(step, 5)}>
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div key={i} className={`h-1 flex-1 rounded ${i <= step ? 'bg-mainBlue' : 'bg-gray-200'}`} />
              ))}
            </div>
          </div>

          {step === 0 && (
            <div>
              <label htmlFor="questionnaire-child-name" className="block text-sm font-medium text-black mb-2">1. Child&apos;s Name</label>
              <input
                id="questionnaire-child-name"
                maxLength={120}
                value={childName}
                onChange={e => setChildName(e.target.value)}
                className="min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue"
                placeholder="Enter your child's name"
              />
            </div>
          )}

          {step === 1 && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">2. What is your child&apos;s primary goal?</label>
              <p className="text-xs text-gray-500 mb-3">Select all that apply</p>
              <div className="space-y-2">
                {goalOptions.map(g => (
                  <label key={g} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={goals.includes(g)}
                      onChange={() => toggleGoal(g)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">{g}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">3. When would you like to see meaningful progress?</label>
              <div className="space-y-2">
                {timelineOptions.map(t => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="timeline"
                      checked={timeline === t}
                      onChange={() => setTimeline(t)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">{t}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">4. How often can your child attend ETA sessions?</label>
              <div className="space-y-2">
                {frequencyOptions.map(f => (
                  <label key={f} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="frequency"
                      checked={frequency === f}
                      onChange={() => setFrequency(f)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">{f}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">5. How much support can you provide at home?</label>
              <div className="space-y-2">
                {supportOptions.map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="support"
                      checked={support === s}
                      onChange={() => setSupport(s)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">{s}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h3 className="text-lg font-semibold text-black mb-2">Program Placement</h3>
              <p className="text-sm text-gray-700 mb-4">
                Your child&apos;s age provides a baseline for program placement. Your answers and our free assessment will determine the best level within a specific ETA program, based on cognitive readiness.
              </p>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>ETA Tots:</strong> 18 months–2 years</p>
                <p><strong>ETA Tinies:</strong> 2–7 years</p>
                <p><strong>ETA Tykes:</strong> 7–11 years</p>
                <p><strong>ETA Tweens/Teens:</strong> 11+ years</p>
              </div>
              <p className="mt-4 text-xs text-gray-500">
                Programs are broken up based on Psychological Phases of Cognitive Development. We cannot move children into age categories beyond these boundaries. Programs are made to sufficiently challenge advanced learners in a holistic manner, while preparing them for their upcoming years.
              </p>
              {status === 'sent' && (
                <p className="mt-4 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  Thanks! We got your questionnaire — we'll be in touch.
                </p>
              )}
            </div>
          )}

          {status === 'error' && (
            <p role="alert" className="mt-4 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              {errorMessage}
            </p>
          )}

          <div className="flex justify-between mt-6">
            {step > 0 ? (
              <button type="button" onClick={prev} className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50">
                Back
              </button>
            ) : (
              <div />
            )}
            {step < 5 ? (
              <button type="button" onClick={next} className="px-6 py-2 bg-mainPink text-white rounded hover:bg-mainBlue transition-colors">
                Next
              </button>
            ) : (
              <button type="submit" disabled={status === 'sending' || status === 'sent'} className="px-6 py-2 bg-mainBlue text-white rounded hover:bg-mainBlue/80 transition-colors disabled:opacity-60">
                {status === 'sending' ? 'Sending…' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default Questionnaire;